import { AsyncPipe, CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  Optional,
  Self,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { BehaviorSubject, Observable, of, switchMap, tap } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  withLatestFrom
} from 'rxjs/operators';
import { ConfigService } from '../../services/config.service';
import { SearchService } from '../../services/search.service';

@Component({
  selector: 'app-address-search-field',
  templateUrl: './address-search-field.component.html',
  providers: [],
  standalone: true,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatIcon,
    MatSuffix,
    MatAutocomplete,
    MatOption,
    MatIconButton,
    AsyncPipe,
    CommonModule,
    MatError
  ]
})
export class AddressSearchFieldComponent
  implements ControlValueAccessor, OnInit
{
  onChange: any = () => {};
  onTouched: any = () => {};

  value: { address: string; place_id: string } | null = null;
  searchField = new FormControl('', [Validators.required]);
  destroyRef = inject(DestroyRef);
  searchResults$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  status$ = new BehaviorSubject<'idle' | 'loading' | 'no results'>('idle');

  constructor(
    private searchService: SearchService,
    private configService: ConfigService,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    // Override the parent control's markAsTouched method
    if (this.ngControl?.control) {
      const originalMarkAsTouched = this.ngControl.control.markAsTouched;
      this.ngControl.control.markAsTouched = (opts?: any) => {
        originalMarkAsTouched.call(this.ngControl.control, opts);
        this.searchField.markAsTouched();
      };
    }

    this.searchField.valueChanges
      .pipe(
        tap((searchTerm) => console.log('searchTerm', searchTerm)),
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        tap(() => this.status$.next('idle')),
        filter((searchTerm) => searchTerm && searchTerm.length > 2),
        tap(() => this.status$.next('loading')),
        withLatestFrom(this.configService.currentLocation),
        switchMap(([searchTerm, location]) =>
          this.searchAddress(searchTerm, location.id)
        ),
        catchError((err) => {
          return of([]);
        }),
        tap((results) =>
          this.status$.next(results.length === 0 ? 'no results' : 'idle')
        ),
        tap((results) => this.searchResults$.next(results))
      )
      .subscribe();
  }

  private searchAddress(searchTerm, locationId): Observable<any[]> {
    if (!searchTerm) {
      return of([]);
    }
    return this.searchService
      .addressSearch(searchTerm, locationId)
      .pipe(catchError(() => []));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    this.value = obj;
    if (obj) {
      this.searchField.setValue(obj.address || '');
    } else {
      this.searchField.reset();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchField.disable();
    } else {
      this.searchField.enable();
    }
  }

  markAsTouched(): void {
    this.searchField.markAsTouched();
    this.onTouched();
  }

  markAsDirty(): void {
    this.searchField.markAsDirty();
  }

  get hasError(): boolean {
    // Check if parent control is invalid and touched, or if our internal field is invalid and touched
    return (
      (this.ngControl?.invalid && this.ngControl?.touched) ||
      (this.searchField.invalid && this.searchField.touched)
    );
  }

  get errorMessage(): string {
    if (
      this.ngControl?.hasError('required') ||
      this.searchField.hasError('required')
    ) {
      return 'Address is required';
    }
    return '';
  }

  clearSelected() {
    this.value = null;
    this.searchField.reset();
    this.onChange(null);
  }

  displayFn() {
    return '';
  }

  selectAddress({ option }: MatAutocompleteSelectedEvent) {
    if (option.value) {
      this.value = option.value;
      this.onChange(option.value);
    }
  }
}
